// Defence (decisions 0051, 0059; suggestion "block and parry"): what a contact does to a defender, from what the
// defender is doing and how long ago it started, in whole steps. Pure, so its window edges are unit-tested step by
// step and the spike's scenario spec only has to confirm the same edges in the running game.
//
// - A dodge is invulnerable for its first `invulnSteps` steps; a contact in its first `perfectSteps` steps is a perfect
//   dodge (once per dodge: the reward is slow-mo and a counter; later contacts in the same dodge read as dodged).
// - A block faces the blow (the attacker within the block's arc of the defender's facing); a block started at most
//   `parrySteps` steps before the contact is a parry. Unblockable blows go through a block or a parry as a plain hit.
// - Age 0 is the step the defence started: a dodge or a block pressed on the contact's own step counts.

/** Window lengths in steps at the sim's rate (the game's seconds × its step rate, rounded). */
export interface DefenceWindows {
  /** A block at most this many steps old at contact is a parry (0.15 s at 120 Hz = 18). */
  parrySteps: number;
  /** A dodge is invulnerable while younger than this. */
  invulnSteps: number;
  /** A dodge younger than this at contact is a perfect dodge. */
  perfectSteps: number;
  /** A block covers attackers whose direction has a dot product above this with the defender's facing (0.15: ±81°). */
  blockCos: number;
}

/** What the defender is doing at the contact. */
export interface DefenderNow {
  defending: "none" | "block" | "dodge";
  /** Steps since the defence started (0 on its first step). */
  age: number;
  /** This dodge already paid out a perfect dodge. */
  perfectUsed: boolean;
  /** The defender's facing (rad; 0 faces +Z). */
  yaw: number;
}

/** The outcome of one contact, as `combat:hit` reports it. */
export type DefenceOutcome = "hit" | "blocked" | "parried" | "dodged" | "perfectDodge";

/** Planted defects beside what they break (tests only). */
export interface DefenceFaults {
  /** The parry window is one step shorter: a block started exactly `parrySteps` steps before the hit only blocks. */
  parryWindowShift: boolean;
}

/** Resolve one contact against a defender. `toAttacker` is the horizontal direction from the defender to the attacker. */
export function resolveDefence(
  d: DefenderNow,
  toAttacker: { x: number; z: number },
  unblockable: boolean,
  w: DefenceWindows,
  faults: DefenceFaults = { parryWindowShift: false },
): DefenceOutcome {
  if (d.defending === "dodge" && d.age >= 0 && d.age < w.invulnSteps) {
    return !d.perfectUsed && d.age < w.perfectSteps ? "perfectDodge" : "dodged";
  }
  if (d.defending === "block" && !unblockable && facing(d.yaw, toAttacker) > w.blockCos) {
    // the planted defect sits here, beside the window it shifts
    const parry = faults.parryWindowShift ? w.parrySteps - 1 : w.parrySteps;
    return d.age >= 0 && d.age <= parry ? "parried" : "blocked";
  }
  return "hit";
}

/** The dot product of a facing's forward with a horizontal direction (normalised here). */
export function facing(yaw: number, dir: { x: number; z: number }): number {
  const m = Math.hypot(dir.x, dir.z);
  if (m < 1e-9) return 1;
  return (Math.sin(yaw) * dir.x + Math.cos(yaw) * dir.z) / m;
}

/** Seconds to whole steps at `hz` (rounded). */
export const toSteps = (seconds: number, hz: number): number => Math.round(seconds * hz);
